'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import Autosuggest from 'react-autosuggest';
import autosuggestHighlightMatch from 'autosuggest-highlight/match';
import autosuggestHighlightParse from 'autosuggest-highlight/parse';
import { sprintf } from 'sprintf-js';

import courseSearchStore from './CourseSearchStore';
import queryStringTextFieldStore from './QueryStringTextFieldStore';
import { MetricLabels } from "@uw-it-sis/lib-react-myplan/lib/Constants";

/**
 * Text field with subject area and course title autocomplete.
 *
 * NOTE: In order for this component to work correctly it cannot be re-rendered by its parent after the initial rendering.
 * Otherwise, the cursor will jump to the end of the text after every keystroke. Basically, it needs to manage it's own
 * state and tell the parent (CourseSearchStore) about changes, but CourseSearchStore shouldn't trigger when it handles
 * those events.
 */

class QueryStringTextField extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = queryStringTextFieldStore;

        // We are using the initial state here to avoid the issue with the cursor being moved to the end.
        this.state = {
            value: "",
            suggestions: []
        };

        this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
        this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
        this.onChange = this.onChange.bind(this);
        this.analyticsStore = appStore.getAnalyticsStore();
    }

    componentDidMount() {
        // Anti-pattern, but we really need to pass query string in via props ... and we really need to set value, so ???
        this.setState({
           value: this.props.queryString
        });
        this.store.mount();
    }
    
    componentWillUnmount() {
        this.store.unmount();
    }

    /**
     * This is called every time a key is typed.
     */
    componentDidUpdate(prevProps) {
        if (this.props.queryString !== prevProps.queryString) {
            this.setState({
                value: this.props.queryString
            });
        }
    }

    /**
     * Returns a filtered list of suggestions.
     */
    getSuggestions(value) {
        return this.store.getFilteredSuggestions(value, this.props.campus, this.props.isSectionSearch, this.props.limit);
    }

    /**
     * Extracts the displayed value of a particular suggestion.
     */
    getSuggestionValue(suggestion) {
        // If the suggestion has props quotedCode and title, use the quotedCode as value (subject areas) else use code (gen eds);
        // if suggestion has prop name, use name as value (instructors)
        if ( suggestion.hasOwnProperty("quotedCode") && suggestion.hasOwnProperty("title") ) {
            return suggestion.quotedCode
        }else if ( suggestion.hasOwnProperty("code") && suggestion.hasOwnProperty("title") ) {
            return suggestion.code
        } else if ( suggestion.hasOwnProperty("name") ) {
            return suggestion.name;
        } else {
            return "";
        }
    }

    /**
     * Called to determine how to render a selection in the list.
     */
    renderSuggestion(suggestion, { query }) {
        let suggestionText = null;

        if ( suggestion.hasOwnProperty("code") && suggestion.hasOwnProperty("title") ) {
            // If suggestion object has code and title, display suggest text as a combo of both props
            suggestionText = sprintf("%s (%s)", suggestion.code, suggestion.title);
        } else if ( suggestion.hasOwnProperty("name") ) {
            // If suggestion object has name, display suggest text as name
            suggestionText = suggestion.name;
        } else {
            return null;
        }
        // build a suggestion item that highlights the part of the string that matches the input
        let matches = autosuggestHighlightMatch(suggestionText, query);
        let parts = autosuggestHighlightParse(suggestionText, matches);

        return (
            <>
                {
                    parts.map((part, index) => {
                        return part.highlight ? <strong key={index}>{part.text}</strong> : part.text;
                    })
                }
            </>
        );
    }

    /**
     * Returns a filtered list of suggestions within a section
     */
    getSectionSuggestions(section) {
        return section.data;
    }

    /**
     * Called to determine how to render a section title in the list.
     */
    renderSectionTitle(section) {
        return section.title;
    }

    renderSuggestionsContainer({ containerProps, children }) {
        let {key, ...restContainerProps} = containerProps;
        return (
            <div key={key} {...restContainerProps} aria-label="Search Query Suggestions">
                {children}
            </div>
        );
    }

    /**
     * Called when a suggestion is selected.
     */
    onSuggestionSelected = (e, { suggestion, suggestionValue, sectionIndex, method }) => {
        // Get the title of the section that the suggestion came from
        const sectionTitle = this.state.suggestions[sectionIndex].title;
        // Determine that the selection came from the instructors list
        const isInstructorSearch = ( sectionTitle.toUpperCase() === "Instructors".toUpperCase() );
        // Update the isInstructorSearch flag on the form data
        courseSearchStore.updateCourseSearchFormData("isInstructorSearch", isInstructorSearch);
        courseSearchStore.updateCourseSearchFormData("queryString", suggestionValue);

        const additionalData = {
            "suggestionSelected": suggestionValue.split(" ").join("^"),
            "suggestionSection": sectionTitle.split(" ").join("^"),
            "suggestionMethod": method
        };
        const labels = this.analyticsStore.label(MetricLabels.grouping, sectionTitle.split(" ").join("^")).get();
        this.analyticsStore.addEvent("Search-Autosuggest-Selection", labels, additionalData);

        this.setState({
            value: suggestionValue
        });
    };

    onSuggestionsFetchRequested = (value) => {
        this.setState({
            suggestions: this.getSuggestions(value)
        });
    };

    onSuggestionsClearRequested = () => {
        this.setState({
            suggestions: []
        });
    };

    /**
     * Called when a letter is typed in the text area.
     */
    onChange = (e, { newValue, method }) => {
        //  Ignore targets which are not the textArea (i.e. the suggestions which are handled in onSuggestionSelected)
        if (! e.target.type) {
            return;
        }

        // Let CourseSearchStore know that the query string value has changed.
        courseSearchStore.updateCourseSearchFormData("queryString", newValue);

        // If the change is due to user typing, reset the isInstructorSearch flag, only an instructor autosuggest
        // selection sets the flag to true
        if (method === 'type') {
            courseSearchStore.updateCourseSearchFormData("isInstructorSearch", false);
        }

        // If existing value is not empty, and new value is, fire a clear search event
        let existingTrimLength = ( this.state.value ? this.state.value.trim().length : 0 );
        let newTrimLength = ( newValue ? newValue.trim().length : 0 );

        if ( existingTrimLength !== 0 && newTrimLength === 0 ) {
            courseSearchStore.disableSearch();
        } else if (newTrimLength > 0 && existingTrimLength === 0) {
            courseSearchStore.enableSearch();
        }

        this.setState({
            value: newValue
        });
    };

    render() {
        const { value, suggestions } = this.state;

        let inputProps = {
            "placeholder": 'e.g. Math 124, ENGL 1xx, COM, 2 credits, A&H, fiction',
            "value": value,
            "onChange": this.onChange,
            "className": "form-control",
            "autoComplete": "off",
            "autoCorrect": "off",
            "autoCapitalize": "off",
            "spellCheck": "false",
            "required": "required",
            "name": this.props.name,
            "id": "search-query",
            "disabled": this.props.disabled
        };

        let maxLengthMessage = null;
        // Validation for max length of text input, check if prop is set
        if ( this.props.maxLength ) {
            // Add prop value to input props
            inputProps.maxLength = this.props.maxLength;
            // If the input length is currently within 10 characters of the limit, show message
            if ( value && value.length >= (this.props.maxLength - 10) ) {
                let maxlengthReached = this.props.maxLength === value.length;
                let lengthRemaining = this.props.maxLength - value.length;
                let className = maxlengthReached ? "danger":"warning";
                // Append style class to input props
                inputProps.className += " input-maxlength-" + className;
                maxLengthMessage =
                    <label variant={className}>
                        {maxlengthReached ? "Reached the maximum length": sprintf("%s character%s left", lengthRemaining, ( lengthRemaining !== 1 ? "s":"" ))}
                    </label>;
            }
        }

        return (
            <>
                <Autosuggest
                    id="search-query"
                    multiSection={true}
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    getSuggestionValue={this.getSuggestionValue}
                    onSuggestionSelected={this.onSuggestionSelected}
                    renderSuggestion={this.renderSuggestion}
                    renderSectionTitle={this.renderSectionTitle}
                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                    getSectionSuggestions={this.getSectionSuggestions}
                    inputProps={inputProps} />
                {maxLengthMessage}
            </>
        );
    }
}

QueryStringTextField.defaultProps = {
    limit: false
};

QueryStringTextField.propTypes = {
    queryString: PropTypes.string,
    name: PropTypes.string,
    limit: PropTypes.number,
    campus: PropTypes.string,
    disabled: PropTypes.bool,
    isSectionSearch: PropTypes.bool,
    maxLength: PropTypes.number
};

export default observer(QueryStringTextField);
